
import { useState, useEffect, forwardRef } from "react";

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

const LazyImage = forwardRef<HTMLImageElement, LazyImageProps>(
  ({ src, alt, className, width, height, loading = "lazy", ...props }, ref) => {
    const [imageSrc, setImageSrc] = useState<string>("/placeholder.svg");
    const [isLoading, setIsLoading] = useState(true);
    const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);
    const [hasError, setHasError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 3;

    useEffect(() => {
      let observer: IntersectionObserver;
      let didCancel = false;
      let imageToLoad: HTMLImageElement;

      const tryLoadImage = (imagePath: string) => {
        return new Promise<void>((resolve, reject) => {
          imageToLoad = new Image();
          
          const timeoutId = setTimeout(() => {
            reject(new Error('Image load timeout'));
          }, 10000); // 10 second timeout

          imageToLoad.onload = () => {
            clearTimeout(timeoutId);
            resolve();
          };
          
          imageToLoad.onerror = () => {
            clearTimeout(timeoutId);
            reject();
          };
          
          imageToLoad.src = imagePath;
        });
      };

      const loadImage = async () => {
        if (!didCancel) {
          setIsLoading(true);
          try {
            // Try loading WebP version first
            const webpPath = src.replace(/\.(jpg|jpeg|png)$/, '.webp');
            await tryLoadImage(webpPath);
            if (!didCancel) setImageSrc(webpPath);
          } catch {
            try {
              // Try original format
              await tryLoadImage(src);
              if (!didCancel) setImageSrc(src);
            } catch {
              console.error(`Failed to load image: ${src}`);
              if (!didCancel) {
                if (retryCount < MAX_RETRIES) {
                  setRetryCount(prev => prev + 1);
                  setTimeout(loadImage, 1000 * (retryCount + 1)); // Exponential backoff
                } else {
                  setHasError(true);
                  setImageSrc("/placeholder.svg");
                }
              }
            }
          } finally {
            if (!didCancel) setIsLoading(false);
          }
        }
      };

      if (imageRef && loading === "lazy") {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (
                !didCancel &&
                (entry.intersectionRatio > 0 || entry.isIntersecting)
              ) {
                loadImage();
                observer.unobserve(imageRef);
              }
            });
          },
          {
            threshold: 0.01,
            rootMargin: "75%",
          }
        );
        observer.observe(imageRef);
      } else if (loading === "eager") {
        loadImage();
      }

      return () => {
        didCancel = true;
        if (observer && imageRef) {
          observer.unobserve(imageRef);
        }
        if (imageToLoad) {
          imageToLoad.onload = null;
          imageToLoad.onerror = null;
        }
      };
    }, [src, imageRef, retryCount, loading]);

    return (
      <div className="relative overflow-hidden" style={{ aspectRatio: width && height ? `${width}/${height}` : 'auto' }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="animate-pulse bg-gray-200 w-full h-full" />
          </div>
        )}
        {hasError && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-sm text-gray-500">Failed to load image</div>
          </div>
        )}
        <img
          ref={(node) => {
            setImageRef(node);
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          onError={() => {
            console.error(`Image load error for: ${src}`);
            setHasError(true);
            setImageSrc("/placeholder.svg");
            setIsLoading(false);
          }}
          loading={loading}
          {...props}
        />
      </div>
    );
  }
);

LazyImage.displayName = "LazyImage";

export default LazyImage;
